
"use client";

import React, { useEffect, useMemo, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-charts-community";
import "./agGrids.css";
import CustomNoRowsOverlay from "./CustomNoRowsOverlay";
import {
    ClientSideRowModelModule,
    CsvExportModule,
    NumberFilterModule,
    TextFilterModule,
    ValidationModule,
    ModuleRegistry,
} from "ag-grid-community";
import {
    ColumnMenuModule,
    ContextMenuModule,
    ExcelExportModule,
    MultiFilterModule,
    SetFilterModule,
    SideBarModule,
    GridChartsModule,
    RangeSelectionModule,
    RowGroupingModule,
    MenuModule,
    FiltersToolPanelModule,
    ColumnsToolPanelModule,
    StatusBarModule,
    RichSelectModule,
} from "ag-grid-enterprise";

// Register AG Grid modules
ModuleRegistry.registerModules([
    TextFilterModule,
    NumberFilterModule,
    ClientSideRowModelModule,
    CsvExportModule,
    ExcelExportModule,
    ColumnMenuModule,
    ContextMenuModule,
    MultiFilterModule,
    SetFilterModule,
    ValidationModule,
    SideBarModule,
    GridChartsModule,
    RangeSelectionModule,
    RowGroupingModule,
    MenuModule,
    FiltersToolPanelModule,
    ColumnsToolPanelModule,
    StatusBarModule,
    RichSelectModule,
]);

const GridExample = () => {
    const gridRef = useRef();
    const gridContainerRef = useRef(null);
    const containerStyle = useMemo(() => ({ width: "100%", height: "100%", padding: "20px", position: "relative" }), []);
    const gridStyle = useMemo(() => ({
        height: "600px",
        width: "100%",
        padding: "20px",
        border: "1px solid #ddd",
        borderRadius: "8px",
        background: "#fff",
        boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
    }), []);
    const [rowData, setRowData] = useState([]);
    const [columnDefs, setColumnDefs] = useState([]);
    const [skip, setSkip] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const pageSize = 10;

    const defaultColDef = {
        resizable: true,
        sortable: true,
        filter: true,
        floatingFilter: true,
        enableRowGroup: true,
        enablePivot: true,
        enableValue: true,
        cellStyle: { padding: "10px" },
        tooltipValueGetter: (params) => params.value,
    };

    const gridOptions = {
        pivotMode: false,
        suppressScrollOnNewData: true, // Prevent grid from scrolling to top on new data
    };

    const formatDateString = (value) => {
        if (typeof value === "string" && value.includes("T")) {
            return value.split("T")[0];
        }
        return value;
    };

    // Fetch raw data from API
    const fetchRawData = async (filterModel = null) => {
        if (isLoading) {
            console.log("Fetch skipped: isLoading is true");
            return null;
        }
        setIsLoading(true);
        console.log(`Fetching data: skip=${skip}, top=${pageSize}`);

        try {
            let url = `https://lensetesting.quantapeople.com/reports/get_data_after_marts_with_skip_top?input_id=67a45bc5cf384697019f644b&skip=${skip}&top=${pageSize}`;

            if (filterModel && Object.keys(filterModel).length > 0) {
                const filterParams = encodeURIComponent(JSON.stringify(filterModel));
                url += `&filters=${filterParams}`;
            }

            console.log("API URL:", url);
            console.log("Fetch start");
            const response = await fetch(url);
            const data = await response.json();
            console.log("Raw API Response:", data);
            return data;
        } catch (error) {
            console.error("Error fetching data:", error);
            return null;
        } finally {
            setIsLoading(false);
            console.log("Fetch complete: isLoading set to false");
        }
    };

    // Add this useEffect to inject dynamic styles
    useEffect(() => {
        const styleId = 'dynamic-header-colors';

        // Remove existing style if it exists
        const existingStyle = document.getElementById(styleId);
        if (existingStyle) {
            existingStyle.remove();
        }

        // Create and inject new styles
        const style = document.createElement('style');
        style.id = styleId;
        style.textContent = `
        
    `;
        document.head.appendChild(style);

        // Cleanup function
        return () => {
            const styleToRemove = document.getElementById(styleId);
            if (styleToRemove) {
                styleToRemove.remove();
            }
        };
    }, []);


    const [headerColors, setHeaderColors] = useState({
        firstRow: '#e3f2fd',    // Light blue
        secondRow: '#f3e5f5'    // Light purple
    });

    // Then use these colors in your dynamic CSS injection

    // Process fetched data and update grid
    const processData = (data, filterModel = null, reset = false) => {
        console.log("Processing data");
        if (!Array.isArray(data) || data.length === 0) {
            console.log("No data returned or data is not an array:", data);
            if (reset) {
                setRowData([]);
                setColumnDefs([]);
            }
            return;
        }

        let filteredData = data;
        if (filterModel && Object.keys(filterModel).length > 0) {
            console.log("Applying client-side filter with model:", filterModel);
            filteredData = data.filter((record) => {
                return Object.keys(filterModel).every((field) => {
                    const filter = filterModel[field];
                    const fieldValue = record["people data mart"]?.[field];

                    console.log(`Checking field "${field}" with value "${fieldValue}" against filter:`, filter);

                    if (fieldValue === undefined || fieldValue === null) {
                        console.log(`Field "${field}" is undefined or null, excluding record.`);
                        return false;
                    }

                    if (filter.filterType === "text" && filter.type === "contains") {
                        const matches = fieldValue.toLowerCase().includes(filter.filter.toLowerCase());
                        console.log(`Does "${fieldValue}" contain "${filter.filter}"? ${matches}`);
                        return matches;
                    }

                    console.log(`Unsupported filter type for field "${field}":`, filter);
                    return true;
                });
            });
            console.log("Filtered Data:", filteredData);
        }

        if (filteredData.length === 0) {
            console.log("No records match the filter criteria.");
            if (reset) {
                setRowData([]);
                setColumnDefs([]);
            }
            return;
        }

        let transformedData = [];
        let columnGroups = {};

        filteredData.forEach((emp, index) => {
            let flatObj = { employee_id: emp.employee_id };
            console.log(`Transforming record ${index + 1}:`, emp);

            Object.keys(emp).forEach((key) => {
                if (typeof emp[key] === "object" && emp[key] !== null) {
                    if (key.toLowerCase().includes("calendar month")) {
                        if (!columnGroups["Performance Metrics"]) columnGroups["Performance Metrics"] = [];
                        Object.entries(emp[key]).forEach(([month, value]) => {
                            let columnName = `${month} (${key.replace("calendar month ", "")})`;
                            flatObj[columnName] = value;
                            if (!columnGroups["Performance Metrics"].includes(columnName)) {
                                columnGroups["Performance Metrics"].push(columnName);
                            }
                        });
                    } else {
                        Object.entries(emp[key]).forEach(([nestedKey, value]) => {
                            const fieldKey = nestedKey.replace(/\s+/g, "_");
                            if (nestedKey === "Employee Name") {
                                flatObj[fieldKey] = (value || "Unknown").trim();
                            } else if (nestedKey === "Gender") {
                                const gender = (value || "").trim().toLowerCase();
                                flatObj[fieldKey] = gender.charAt(0).toUpperCase() + gender.slice(1);
                            } else {
                                flatObj[fieldKey] = formatDateString(value);
                            }
                            if (!columnGroups[key]) columnGroups[key] = [];
                            if (!columnGroups[key].includes(fieldKey)) {
                                columnGroups[key].push(fieldKey);
                            }
                        });
                    }
                } else {
                    flatObj[key] = formatDateString(emp[key]);
                }
            });

            if (!columnGroups["General Info"]) {
                columnGroups["General Info"] = [];
            }
            if (!columnGroups["General Info"].includes("employee_id")) {
                columnGroups["General Info"].unshift("employee_id");
            }

            console.log(`Transformed record ${index + 1}:`, flatObj);
            transformedData.push(flatObj);
        });

        let dynamicColumns = Object.keys(columnGroups).map((group) => ({
            headerName: group,
            children: columnGroups[group].map((field) => {
                console.log(`Creating column for field "${field}"`);
                const columnDef = {
                    field,
                    headerName: field.replace(/_/g, " "),
                    headerTooltip: field.replace(/_/g, " "),
                    filter: transformedData[0][field] && !isNaN(transformedData[0][field]) ? "agNumberColumnFilter" : "agTextColumnFilter",
                    sortable: true,
                    floatingFilter: true,
                    resizable: true,
                    enablePivot: true,
                    enableRowGroup: true,
                    enableValue: true,
                    aggFunc: transformedData[0][field] && !isNaN(transformedData[0][field]) ? "sum" : "count",
                    chartDataType: transformedData[0][field] && !isNaN(transformedData[0][field]) ? "series" : "category",
                    enableRangeSelection: true,
                    valueFormatter: (params) => {
                        if (typeof params.value === "number") {
                            return params.value.toLocaleString();
                        }
                        return params.value;
                    },
                };
                if (field === "Employee_Name") {
                    columnDef.pinned = "left";
                    columnDef.width = 200;
                    columnDef.headerClass = "highlighted-column";
                }
                return columnDef;
            }),
        }));

        console.log("Transformed Data for Grid:", transformedData);
        console.log("Column Definitions:", dynamicColumns);

        setRowData((prev) => {
            const newData = reset ? transformedData : [...prev, ...transformedData];
            console.log(`Updating rowData: ${reset ? "Resetting" : "Appending"} ${transformedData.length} rows, total ${newData.length} rows`);
            return newData;
        });
        if (reset || columnDefs.length === 0) {
            setColumnDefs(dynamicColumns);
        }
        if (!reset) {
            setSkip((prev) => prev + pageSize);
        }
    };

    // Combined fetch and process logic
    const fetchData = async (filterModel = null, reset = false) => {
        const data = await fetchRawData(filterModel);
        if (data) {
            processData(data, filterModel, reset);
        }
    };

    useEffect(() => {
        fetchData(null, true); // Initial load with reset
    }, []);

    const onGridReady = (params) => {
        gridRef.current = { api: params.api, columnApi: params.columnApi };
        console.log("Grid initialized: API ready");

        if (!params.columnApi) {
            console.warn("columnApi is undefined");
            return;
        }

        setTimeout(() => {
            const allColumnIds = params.columnApi.getAllColumns()?.map((column) => column.getId()) || [];
            if (allColumnIds.length > 0) {
                params.columnApi.autoSizeColumns(allColumnIds);
            }
        }, 100);
    };

    useEffect(() => {
        if (gridRef.current && gridRef.current.columnApi) {
            const allColumnIds = gridRef.current.columnApi.getAllColumns()?.map((col) => col.getId()) || [];
            if (allColumnIds.length > 0) {
                gridRef.current.columnApi.autoSizeColumns(allColumnIds);
            }
        }
    }, [rowData]);

    const onFilterChanged = (params) => {
        const filterModel = params.api.getFilterModel();
        console.log("Filter model changed:", filterModel);
        setSkip(0);
        fetchData(filterModel, true); // Reset data on filter change
    };

    // Scroll event handler for lazy loading
    useEffect(() => {
        if (!gridContainerRef.current || !gridRef.current?.api) {
            console.log("Skipping scroll listener setup: gridContainerRef or grid API not ready");
            return;
        }

        const viewport = gridContainerRef.current.querySelector(".ag-body-viewport") ||
            gridContainerRef.current.querySelector(".ag-body") ||
            gridContainerRef.current.querySelector(".ag-center-cols-viewport");
        if (!viewport) {
            console.warn("Scroll viewport element not found. Tried .ag-body-viewport, .ag-body, .ag-center-cols-viewport");
            return;
        }

        console.log("Scroll listener attached to viewport");

        const handleScroll = () => {
            const scrollTop = viewport.scrollTop;
            const scrollHeight = viewport.scrollHeight;
            const clientHeight = viewport.clientHeight;

            console.log(`Scroll event: scrollTop=${scrollTop}, clientHeight=${clientHeight}, scrollHeight=${scrollHeight}`);

            if (scrollTop + clientHeight >= scrollHeight - 100 && !isLoading) {
                console.log("Reached bottom, triggering fetchData");
                const filterModel = gridRef.current.api.getFilterModel();
                fetchData(filterModel, false); // Append data, no reset
            }
        };

        viewport.addEventListener("scroll", handleScroll);

        return () => {
            console.log("Cleaning up scroll listener");
            viewport.removeEventListener("scroll", handleScroll);
        };
    }, [isLoading, gridRef.current?.api]);

    const chartThemeOverrides = {
        common: {
            title: {
                enabled: true,
                fontSize: 14,
            },
            legend: {
                position: "bottom",
            },
        },
    };

    return (
        <div style={containerStyle}>
            <div className="ag-theme-alpine" style={gridStyle} ref={gridContainerRef}>
                <AgGridReact
                    ref={gridRef}
                    rowData={rowData}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    {...gridOptions}
                    pagination={false}
                    rowSelection="multiple"
                    enableRangeSelection={true}
                    animateRows={true}
                    onGridReady={onGridReady}
                    onFilterChanged={onFilterChanged}
                    enableCharts={true}
                    sideBar={false}
                    groupDisplayType="multipleColumns"
                    rowGroupPanelShow="always"
                    pivotPanelShow="always"
                    chartThemeOverrides={chartThemeOverrides}
                    enableRangeHandle={true}
                    suppressMultiRangeSelection={false}
                    allowDragFromColumnsToolPanel={true}
                    tooltipShowDelay={0}
                    noRowsOverlayComponent={() => <CustomNoRowsOverlay message="No users found" />}
                />
            </div>
            {isLoading && (
                <div
                    style={{
                        position: "absolute",
                        bottom: "20px",
                        left: "50%",
                        transform: "translateX(-50%)",
                        textAlign: "center",
                        padding: "10px",
                        background: "rgba(255, 255, 255, 0.8)",
                        borderRadius: "8px",
                    }}
                >
                    <div
                        style={{
                            border: "4px solid #f3f3f3",
                            borderTop: "4px solid #AD0101",
                            borderRadius: "50%",
                            width: "24px",
                            height: "24px",
                            animation: "spin 1s linear infinite",
                            margin: "0 auto",
                        }}
                    ></div>
                    <p>Loading more data...</p>
                </div>
            )}
            <style jsx>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

export default GridExample;